import type { DragEvent } from 'react';

export const WORKSPACE_DRAG_MIME = 'application/x-finz-workspace';

/** Safari / some browsers omit custom MIME on ``getData`` at drop time; mirror payload here. */
const WORKSPACE_DRAG_TEXT_PREFIX = 'finz-workspace:';

function utf8ToB64(s: string): string {
    const bytes = new TextEncoder().encode(s);
    let binary = '';
    bytes.forEach((b) => {
        binary += String.fromCharCode(b);
    });
    return btoa(binary);
}

function b64ToUtf8(b64: string): string {
    const binary = atob(b64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    return new TextDecoder().decode(bytes);
}

/** Delay (ms) before hover navigates into a folder or breadcrumb segment while dragging workspace items. */
export const WORKSPACE_HOVER_NAV_MS = 1000;

export type WorkspaceDragPayload =
    | { kind: 'files'; fileIds: string[]; sourceCategoryKey?: string }
    | { kind: 'folder'; sourceFolderPath: string; sourceCategoryKey?: string };

function setWorkspaceDragPreview(dt: DataTransfer, label: string) {
    if (typeof document === 'undefined') return;
    const preview = document.createElement('div');
    preview.textContent = label;
    preview.style.position = 'fixed';
    preview.style.top = '-9999px';
    preview.style.left = '-9999px';
    preview.style.padding = '8px 12px';
    preview.style.borderRadius = '999px';
    preview.style.border = '1px solid rgba(209, 213, 219, 1)';
    preview.style.background = 'rgba(255, 255, 255, 0.98)';
    preview.style.boxShadow = '0 10px 30px rgba(15, 23, 42, 0.14)';
    preview.style.color = '#111827';
    preview.style.fontSize = '13px';
    preview.style.fontWeight = '500';
    preview.style.lineHeight = '1';
    preview.style.pointerEvents = 'none';
    preview.style.maxWidth = '240px';
    preview.style.whiteSpace = 'nowrap';
    preview.style.overflow = 'hidden';
    preview.style.textOverflow = 'ellipsis';
    preview.style.zIndex = '2147483647';
    document.body.appendChild(preview);
    dt.setDragImage(preview, 12, 12);
    window.setTimeout(() => {
        preview.remove();
    }, 0);
}

export function setWorkspaceDragData(dt: DataTransfer, payload: WorkspaceDragPayload, previewLabel?: string) {
    const json = JSON.stringify(payload);
    dt.setData(WORKSPACE_DRAG_MIME, json);
    // Mirror for browsers that drop custom MIME data on ``getData`` at drop.
    try {
        dt.setData('text/plain', `${WORKSPACE_DRAG_TEXT_PREFIX}${utf8ToB64(json)}`);
    } catch {
        /* ignore */
    }
    // Chat drop targets use ``dropEffect = 'copy'`` for uploads; allow both copy and move.
    dt.effectAllowed = 'copyMove';
    if (previewLabel?.trim()) {
        setWorkspaceDragPreview(dt, previewLabel.trim());
    }
}

export function isWorkspaceDragEvent(e: DragEvent): boolean {
    return Array.from(e.dataTransfer?.types || []).includes(WORKSPACE_DRAG_MIME);
}

export function parseWorkspaceDragPayload(e: DragEvent): WorkspaceDragPayload | null {
    try {
        let raw = e.dataTransfer.getData(WORKSPACE_DRAG_MIME)?.trim();
        if (!raw) {
            const plain = e.dataTransfer.getData('text/plain')?.trim() || '';
            if (plain.startsWith(WORKSPACE_DRAG_TEXT_PREFIX)) {
                const b64 = plain.slice(WORKSPACE_DRAG_TEXT_PREFIX.length).trim();
                raw = b64 ? b64ToUtf8(b64).trim() : '';
            }
        }
        if (!raw) return null;
        const parsed = JSON.parse(raw) as WorkspaceDragPayload;
        if (
            parsed?.kind === 'files' &&
            Array.isArray(parsed.fileIds) &&
            (parsed.sourceCategoryKey === undefined || typeof parsed.sourceCategoryKey === 'string')
        ) {
            return parsed;
        }
        if (
            parsed?.kind === 'folder' &&
            typeof parsed.sourceFolderPath === 'string' &&
            (parsed.sourceCategoryKey === undefined || typeof parsed.sourceCategoryKey === 'string')
        ) {
            return parsed;
        }
        return null;
    } catch {
        return null;
    }
}
